import React, { useState } from 'react';

// ----- Types -----

type NotificationType =
  | 'connection_request'
  | 'connection_accepted'
  | 'reaction'
  | 'reply'
  | 'direct_message'
  | 'security_alert';

interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  read: boolean;
  createdAt: string;
}

// ----- Mock data -----
const MOCK_NOTIFICATIONS: Notification[] = [];

// ----- Main Page -----

/**
 * NotificationsPage — ordered list of notifications with unread indicator,
 * click-to-mark-read, and "Mark all read" button.
 */
export function NotificationsPage(): React.JSX.Element {
  const [notifications, setNotifications] = useState<Notification[]>(MOCK_NOTIFICATIONS);

  function handleMarkRead(id: string) {
    // TODO: call POST /notifications/:id/read
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  }

  function handleMarkAllRead() {
    // TODO: call POST /notifications/read-all
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div className="px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
      <div className="mx-auto max-w-2xl">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text-primary">Notifications</h1>
            {unreadCount > 0 && (
              <p className="mt-1 text-sm text-text-secondary">
                {unreadCount} unread
              </p>
            )}
          </div>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="rounded border border-border px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-muted"
            >
              Mark all read
            </button>
          )}
        </div>

        {/* Notifications list */}
        {notifications.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-8 text-center">
            <p className="text-sm text-text-muted">No notifications yet.</p>
          </div>
        ) : (
          <ul className="flex flex-col gap-1">
            {notifications.map((notification) => (
              <li key={notification.id}>
                <button
                  onClick={() => !notification.read && handleMarkRead(notification.id)}
                  className={`flex w-full items-start gap-3 rounded-lg px-4 py-3 text-left transition hover:bg-surface-muted ${
                    !notification.read ? 'bg-byron-light' : ''
                  }`}
                >
                  {/* Unread indicator */}
                  <div className="mt-1.5 flex shrink-0 items-center">
                    {!notification.read ? (
                      <span className="h-2 w-2 rounded-full bg-byron" aria-label="Unread" />
                    ) : (
                      <span className="h-2 w-2" />
                    )}
                  </div>

                  {/* Icon */}
                  <div className="mt-0.5 shrink-0">
                    <NotificationIcon type={notification.type} />
                  </div>

                  {/* Content */}
                  <div className="min-w-0 flex-1">
                    <p
                      className={`text-sm ${
                        !notification.read
                          ? 'font-semibold text-text-primary'
                          : 'text-text-primary'
                      }`}
                    >
                      {notification.title}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-text-muted">
                      {notification.body}
                    </p>
                    <p className="mt-1 text-[10px] text-text-muted">{notification.createdAt}</p>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ----- Notification Icons -----

function NotificationIcon({ type }: { type: NotificationType }) {
  const baseClass = 'h-5 w-5';

  switch (type) {
    case 'connection_request':
      return (
        <svg className={`${baseClass} text-gail`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
        </svg>
      );
    case 'connection_accepted':
      return (
        <svg className={`${baseClass} text-gail`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
    case 'reaction':
      return (
        <svg className={`${baseClass} text-olympia`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      );
    case 'reply':
      return (
        <svg className={`${baseClass} text-byron`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
        </svg>
      );
    case 'direct_message':
      return (
        <svg className={`${baseClass} text-byron`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      );
    case 'security_alert':
      return (
        <svg className={`${baseClass} text-agent-red`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      );
  }
}
